import React, { useState } from 'react';

const Home = () => {
  const [activeTab, setActiveTab] = useState(0);

  const stats = [
    { value: '7.5M', label: 'Users save money with TheCouponers' },
    { value: '$156.80', label: 'saved per user each year through coupons' },
    { value: '50,000+', label: 'stores with verified promo codes' },
    { value: '$285.40', label: 'saved per user each year through price comparison' },
  ];

  const stores = [
    { name: 'Amazon', codes: '234 codes', color: 'bg-orange-100', initial: 'A' },
    { name: 'Target', codes: '156 codes', color: 'bg-red-100', initial: 'T' },
    { name: 'Walmart', codes: '189 codes', color: 'bg-blue-100', initial: 'W' },
    { name: 'Best Buy', codes: '98 codes', color: 'bg-yellow-100', initial: 'BB' },
    { name: "Macy's", codes: '145 codes', color: 'bg-red-100', initial: 'M' },
    { name: 'Sephora', codes: '112 codes', color: 'bg-pink-100', initial: 'S' },
    { name: "Lowe's", codes: '87 codes', color: 'bg-blue-100', initial: 'L' },
    { name: 'Nike', codes: '67 codes', color: 'bg-gray-100', initial: 'N' },
    { name: 'eBay', codes: '203 codes', color: 'bg-yellow-100', initial: 'eB' },
  ];

  const extensionFeatures = [
    {
      tab: 'While Browsing Products',
      title: 'Smart Price Comparison',
      description: "Looking at an item? We'll automatically check other stores to see if there's a better price - no extra work for you.",
    },
    {
      tab: 'At Cart',
      title: 'Coupon Discovery',
      description: "Added items to your cart? We'll show you all available coupons for that store so you can save before checkout.",
    },
    {
      tab: 'At Checkout',
      title: 'Automatic Code Testing',
      description: "At checkout, we automatically test every available coupon code to find the one that saves you the most money.",
    },
  ];

  const steps = [
    {
      step: 'Step 1',
      title: 'Install the free browser extension',
      description: "Install TheCouponers with just a few clicks — it's quick, easy, and completely free. No setup, no hassle. Available on Chrome, Safari, Firefox and Edge.",
    },
    {
      step: 'Step 2',
      title: 'Shop as usual, get smart alerts',
      description: 'TheCouponers detects supported sites and shows you the best coupons with a timely, helpful popup - so you never miss a chance to save.',
    },
    {
      step: 'Step 3',
      title: "We'll Do the Work",
      description: 'TheCouponers applies the best coupons at checkout - saving money is just one click away.',
    },
  ];

  const testimonials = [
    {
      quote: "TheCouponers works to find the best deals online, all in the background. I often forget to check, or just don't want to shop different sites for the best deal. TheCouponers does that for me (seamlessly)!",
      author: 'Michael R.',
      date: '12/15/2025',
      rating: 5,
    },
    {
      quote: "TheCouponers is great! I always save so much money. I tend to shop on my phone but then I go to my laptop so I can use TheCouponers. Just saved $12 on something.",
      author: 'Sarah K.',
      date: '12/10/2025',
      rating: 5,
    },
    {
      quote: "TheCouponers makes finding discounts a breeze! No more hunting and searching for coupons. Have the extension search and apply - simple and will save you money. Why spend more?",
      author: 'Jennifer M.',
      date: '12/08/2025',
      rating: 5,
    },
    {
      quote: "This is great. The extension popped up as I was completing a purchase in the cart and it tried several codes automatically and saved me $11.99 on a $60.00 purchase. I didn't have to lift a finger! Great extension!",
      author: 'David L.',
      date: '12/05/2025',
      rating: 5,
    },
    {
      quote: "Wow! The store had lesser cost compared to another site and once TheCouponers codes are applied, it's almost half the price after coupon, thank you!",
      author: 'Amanda C.',
      date: '11/28/2025',
      rating: 5,
    },
    {
      quote: "This has been so convenient. It has made saving easy and even if I happen to have a code that is on the site, it already has it. Great savings and easy to use!",
      author: 'Robert P.',
      date: '11/25/2025',
      rating: 5,
    },
  ];

  return (
    <main>
      {/* Hero Section */}
      <section className="pt-8 pb-12 md:pt-12 md:pb-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Smart shoppers use <span className="text-accent-500">TheCouponers</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              TheCouponers is your AI-powered shopping assistant that helps you save with coupons and price comparisons — all for free.
            </p>
            <div className="flex flex-col items-center space-y-4" id="get-started">
              <button 
                disabled
                className="bg-gray-300 text-gray-500 font-semibold py-3.5 px-8 rounded-full text-lg cursor-not-allowed flex items-center space-x-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C8.21 0 5.831 1.387 4.5 3.5c-.672 1.065-.5 2.5.5 3.5s2.435.328 3.5-.5c.672-1.065 2.21-2 3.5-2s2.828.935 3.5 2c1.065.828 2.5 1 3.5.5s1.172-2.435.5-3.5C18.169 1.387 15.79 0 12 0zm0 24c3.79 0 6.169-1.387 7.5-3.5.672-1.065.5-2.5-.5-3.5s-2.435-.328-3.5.5c-.672 1.065-2.21 2-3.5 2s-2.828-.935-3.5-2c-1.065-.828-2.5-1-3.5-.5s-1.172 2.435-.5 3.5C5.831 22.613 8.21 24 12 24z" />
                </svg>
                <span>Coming Soon</span>
              </button>
              <p className="text-sm text-gray-500 flex items-center space-x-2">
                <span>Trusted by 7M+ users and rated</span>
                <span className="flex items-center text-accent-500 font-semibold">
                  4.8★
                </span>
                <span>by 10,000+ shoppers</span>
              </p>
            </div>
          </div>

          {/* Hero Image - Browser Mockup */}
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl shadow-elevated overflow-hidden border border-gray-200">
              {/* Browser Chrome */}
              <div className="bg-gray-100 px-4 py-3 flex items-center space-x-2 border-b border-gray-200">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="bg-white rounded-lg px-4 py-1.5 text-sm text-gray-500 w-96 text-center border">
                    amazon.com/checkout
                  </div>
                </div>
              </div>
              {/* Browser Content */}
              <div className="p-6 md:p-10 bg-gray-50">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Left - Checkout Form Mockup */}
                  <div className="lg:col-span-2 space-y-4">
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                      <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
                      <div className="space-y-3">
                        <div className="h-10 bg-gray-100 rounded"></div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="h-10 bg-gray-100 rounded"></div>
                          <div className="h-10 bg-gray-100 rounded"></div>
                        </div>
                        <div className="h-10 bg-gray-100 rounded"></div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-20"></div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-gray-100 rounded"></div>
                        <div className="flex-1">
                          <div className="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                        </div>
                        <div className="h-4 bg-gray-200 rounded w-16"></div>
                      </div>
                    </div>
                  </div>
                  {/* Right - Extension Popup */}
                  <div className="bg-white rounded-2xl shadow-xl border-2 border-accent-500 overflow-hidden">
                    <div className="bg-primary-500 text-white p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                        </div>
                        <div>
                          <div className="font-bold">TheCouponers</div>
                          <div className="text-white/80 text-sm">Found 4 coupons!</div>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl border border-green-200">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <div>
                            <div className="font-mono text-sm font-bold text-green-700">SAVE25NOW</div>
                            <div className="text-xs text-green-600">Best savings!</div>
                          </div>
                        </div>
                        <div className="text-green-600 font-bold">-$31.25</div>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
                          <div className="font-mono text-sm text-gray-600">FREESHIP</div>
                        </div>
                        <div className="text-gray-500">-$5.99</div>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
                          <div className="font-mono text-sm text-gray-600">DEAL15</div>
                        </div>
                        <div className="text-gray-500">-$18.75</div>
                      </div>
                      <button disabled className="w-full bg-gray-300 text-gray-500 font-bold py-3 rounded-xl cursor-not-allowed">
                        Coming Soon
                      </button>
                      <p className="text-center text-xs text-gray-500">
                        Extension launching soon...
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              TheCouponers helps millions save money
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Join millions who save automatically with coupons and real-time price comparisons.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-primary-50 to-primary-100/50 rounded-2xl p-6 text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stores Section */}
      <section className="py-16 bg-gray-50" id="stores">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Unlock 50,000+ Store Offers with TheCouponers
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Whether you're ordering food, shopping for clothes, or planning a trip, TheCouponers finds savings for you across thousands of popular sites.
            </p>
          </div>
          
          {/* Store Grid */}
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-4 mb-8">
            {stores.map((store, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all cursor-pointer group"
              >
                <div className={`w-12 h-12 ${store.color} rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                  <span className="font-bold text-gray-700 text-sm">{store.initial}</span>
                </div>
                <div className="text-center">
                  <div className="font-medium text-gray-900 text-sm truncate">{store.name}</div>
                  <div className="text-xs text-accent-500 font-medium mt-1">{store.codes}</div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <button className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-semibold transition-colors">
              <span>Browse more stores</span>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Extension Features Section */}
      <section className="py-16 bg-primary-500" id="how-it-works">
        <div className="container-custom">
          <div className="text-center mb-10">
            <span className="inline-block bg-white/20 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              TheCouponers Extension
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              TheCouponers helps you save at every step of your shopping journey
            </h2>
            <button 
              disabled
              className="bg-gray-400 text-gray-200 font-semibold py-3 px-8 rounded-full cursor-not-allowed mt-4"
            >
              Coming Soon
            </button>
          </div>
          
          {/* Tabs */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/10 rounded-2xl p-2 mb-8">
              <div className="flex flex-col md:flex-row">
                {extensionFeatures.map((feature, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTab(index)}
                    className={`flex-1 py-3 px-4 rounded-xl font-medium text-sm transition-all ${
                      activeTab === index
                        ? 'bg-white text-primary-600 shadow-lg'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {feature.tab}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Tab Content */}
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {extensionFeatures[activeTab].title}
                  </h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    {extensionFeatures[activeTab].description}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl aspect-video flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="w-20 h-20 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      {activeTab === 0 && (
                        <svg className="w-10 h-10 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                        </svg>
                      )}
                      {activeTab === 1 && (
                        <svg className="w-10 h-10 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                      )}
                      {activeTab === 2 && (
                        <svg className="w-10 h-10 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                    </div>
                    <p className="text-gray-500 text-sm">Feature preview</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-16">
            Start Saving in 3 Easy Steps
          </h2>
          
          <div className="space-y-16 md:space-y-24">
            {steps.map((item, index) => (
              <div
                key={index}
                className={`flex flex-col ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                } items-center gap-10 md:gap-16`}
              >
                {/* Image */}
                <div className="flex-1 w-full max-w-lg">
                  <div className={`rounded-3xl p-8 aspect-[4/3] flex items-center justify-center ${
                    index === 0 ? 'bg-gradient-to-br from-blue-100 to-blue-200' :
                    index === 1 ? 'bg-gradient-to-br from-orange-100 to-orange-200' :
                    'bg-gradient-to-br from-green-100 to-green-200'
                  }`}>
                    {index === 0 && (
                      <svg className="w-32 h-32 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    )}
                    {index === 1 && (
                      <svg className="w-32 h-32 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                    )}
                    {index === 2 && (
                      <svg className="w-32 h-32 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </div>
                </div>
                
                {/* Text */}
                <div className="flex-1 max-w-lg">
                  <span className="text-accent-500 font-bold text-sm uppercase tracking-wider">
                    {item.step}
                  </span>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2 mb-4">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-lg leading-relaxed mb-6">
                    {item.description}
                  </p>
                  <button 
                    disabled
                    className="bg-gray-300 text-gray-500 font-semibold py-3 px-6 rounded-full cursor-not-allowed text-sm"
                  >
                    Coming Soon
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Why savers love TheCouponers
            </h2>
            <p className="text-gray-600">
              7+ million people love saving with TheCouponers - rated <span className="text-accent-500 font-semibold">4.8+ stars</span> by shoppers.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <p className="text-gray-700 mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {testimonial.author.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">{testimonial.author}</div>
                      <div className="text-xs text-gray-500">{testimonial.date}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-0.5">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <svg key={i} className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-primary-700 text-white">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-4xl font-bold mb-2">
            Save more with TheCouponers
          </h2>
          <p className="text-2xl md:text-3xl font-bold text-white/90 mb-8">
            don't miss your chance
          </p>
          
          {/* Badge */}
          <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-8">
            <span className="text-yellow-400 mr-2">⭐</span>
            <span className="font-medium">Launching Soon — Be the First to Know</span>
          </div>
          
          <div className="flex flex-col items-center space-y-4">
            <button 
              disabled
              className="bg-gray-400 text-gray-200 font-bold py-4 px-10 rounded-full text-lg cursor-not-allowed"
            >
              Coming Soon
            </button>
            <p className="text-white/70 text-sm">
              No ads. No interruptions. Just real savings — every time you shop.
            </p>
          </div>
        </div>
      </section>

      {/* Mobile App Section - Commented out for now */}
      {/* <section className="py-20 bg-gray-100">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 flex justify-center">
              <div className="relative">
                <div className="w-64 h-[500px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
                  <div className="w-full h-full bg-gradient-to-br from-primary-500 to-primary-700 rounded-[2.5rem] flex items-center justify-center">
                    <div className="text-white text-center p-6">
                      <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                      </div>
                      <div className="font-bold text-xl">TheCouponers</div>
                      <div className="text-white/80 text-sm mt-2">Mobile App</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex-1 max-w-xl">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Get TheCouponers App – Smarter Savings, Wherever You Shop.
              </h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                Save money effortlessly with TheCouponers! Our smart shopping assistant finds the best coupons and helps you shop smarter — all in one app. Download now for free!
              </p>
              
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <div className="bg-white p-4 rounded-xl shadow-sm">
                  <div className="w-28 h-28 bg-gray-200 rounded-lg flex items-center justify-center">
                    <span className="text-gray-400 text-xs text-center">QR Code</span>
                  </div>
                </div>
                
                <div className="flex flex-col gap-3">
                  <a href="#" className="flex items-center space-x-3 bg-black text-white px-5 py-3 rounded-xl hover:bg-gray-800 transition-colors">
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.5 12.5c0-1.58-.79-2.97-2-3.81V7c0-.55-.45-1-1-1s-1 .45-1 1v1.19c-.63-.12-1.29-.19-2-.19s-1.37.07-2 .19V7c0-.55-.45-1-1-1s-1 .45-1 1v1.69c-1.21.84-2 2.23-2 3.81v1h12v-1z"/>
                    </svg>
                    <div className="text-left">
                      <div className="text-xs text-gray-400">Get it on</div>
                      <div className="font-semibold">Google Play</div>
                    </div>
                  </a>
                  <a href="#" className="flex items-center space-x-3 bg-black text-white px-5 py-3 rounded-xl hover:bg-gray-800 transition-colors">
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83"/>
                    </svg>
                    <div className="text-left">
                      <div className="text-xs text-gray-400">Download on the</div>
                      <div className="font-semibold">App Store</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}
    </main>
  );
};

export default Home;
